import React from 'react'
import { Link } from 'react-router-dom'
import { useIntl } from 'react-intl'
import {
    Table,
    TableBody,
    TableCell,
    TableFooter,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Paper,
    LinearProgress,
    Box,
    IconButton,
} from '@mui/material'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import { toMinutesAndSeconds } from '../../../../utils/functions'
import { SectionAnalysisState } from '../../../../features/types'

interface Props {
    analysis: SectionAnalysisState[]
    pageNumber: number
    totalCount: number
    page: number
    rowsPerPage: number
    isFetching: boolean
    typeEnum: string
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
}

const SectionAnalysisTable: React.FC<Props> = ({
    analysis = [],
    pageNumber = 0,
    totalCount = 0,
    page = 0,
    rowsPerPage = 0,
    isFetching = false,
    typeEnum,
    setPage,
    setRowsPerPage,
}) => {
    const { formatMessage } = useIntl()

    // Avoid a layout jump when reaching the last page with empty rows.
    const emptyRows = page > 0 ? Math.max(0, rowsPerPage - analysis.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const getSectionResult = (section: SectionAnalysisState): string => {
        if (typeEnum === 'TEST') {
            return section.result + ' из ' + section.maxResult
        }

        const result =
            toMinutesAndSeconds(section.result) +
            '/' +
            toMinutesAndSeconds(section.maxResult)
        return result
    }

    return (
        <TableContainer
            component={Paper}
            sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
            elevation={0}
        >
            <Table
                sx={{ minWidth: 500 }}
                aria-label="custom pagination table"
            >
                <TableHead sx={{ bgcolor: 'grey.100' }}>
                    <TableRow>
                        <TableCell>{formatMessage({ id: '#' })}</TableCell>

                        <TableCell>
                            {formatMessage({ id: 'fullName' })}
                        </TableCell>

                        <TableCell>{formatMessage({ id: 'group' })}</TableCell>

                        <TableCell>{formatMessage({ id: 'result' })}</TableCell>

                        <TableCell>
                            {formatMessage({ id: 'in_percent' })}
                        </TableCell>

                        <TableCell align="right">
                            {formatMessage({ id: 'actions' })}
                        </TableCell>
                    </TableRow>
                </TableHead>
                {analysis.length ? (
                    <TableBody>
                        {analysis.map((row, index) => (
                            <TableRow key={index}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    width={'10px'}
                                >
                                    {index + 1}
                                </TableCell>
                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row.firstName} {row.lastName}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {row.groupTitle}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    {getSectionResult(row)}
                                </TableCell>

                                <TableCell
                                    component="th"
                                    scope="row"
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                        }}
                                    >
                                        <Box sx={{ width: '80%' }}>
                                            <LinearProgress
                                                variant="determinate"
                                                value={row.percentage}
                                                color={'primary'}
                                                sx={{ mr: 2 }}
                                            />
                                        </Box>

                                        <Box>
                                            {Math.round(row.percentage)} %
                                        </Box>
                                    </Box>
                                </TableCell>

                                <TableCell
                                    style={{ width: 60 }}
                                    align="right"
                                >
                                    <Link
                                        to={''}
                                        // to={`${row.id}`}
                                    >
                                        <IconButton
                                            sx={{ background: '#f3f4f5' }}
                                        >
                                            <img
                                                src={ArrowRightIcon}
                                                alt="details"
                                            />
                                        </IconButton>
                                    </Link>
                                </TableCell>
                            </TableRow>
                        ))}
                        {emptyRows > 0 && (
                            <TableRow style={{ height: 53 * emptyRows }}>
                                <TableCell colSpan={6} />
                            </TableRow>
                        )}
                    </TableBody>
                ) : (
                    !isFetching && (
                        <TableBody>
                            <TableRow>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    align="center"
                                    colSpan={10}
                                >
                                    {formatMessage({ id: 'noData' })}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    )
                )}

                <TableFooter>
                    <TableRow>
                        <TablePagination
                            rowsPerPageOptions={[
                                5,
                                10,
                                25,
                                { label: 'All', value: -1 },
                            ]}
                            colSpan={6}
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={pageNumber}
                            SelectProps={{
                                inputProps: {
                                    'aria-label': 'rows per page',
                                },
                                native: true,
                            }}
                            onPageChange={handleChangePage}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                            ActionsComponent={TablePaginationActions}
                            sx={{ border: 0 }}
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    )
}

export default SectionAnalysisTable
